import { error } from 'react-notification-system-redux'
import _ from 'lodash'
import moment from 'moment'
import * as PubNub from 'pubnub'

import Api from '../../Api'
import ApiV2 from '../../ApiV2'
import { RootState } from '../../appReducer'
import { ChatStatus } from '../../models/ChatStatus'
import { ConnectPaymentStatus } from '../../models/Connect'
import { ChatCenterTab } from '../../models/enums'
import { ErrorRecord } from '../../models/v2/Errors'
import NotificationService from '../../services/NotificationService'
import PubNubService from '../../services/PubNubService'
import { newObjectId } from '../../util/objectId'
import { getGlobalStore } from '../../util/globalStore'

import { postPatientConsentToPaymentTerms, updateConnectPaymentStatus } from './connect/actions'
import { mapChatMetadata } from './reducer'

export type ClearIdleTimer = {
    type: 'CLEAR_IDLE_TIMER'
    channel: string
}

export type ClearInactiveTimer = {
    type: 'CLEAR_INACTIVE_TIMER'
    channel: string
}

export type ReceiveBrowsingCount = {
    type: 'RECEIVE_BROWSING_COUNT'
    count: number
}

export type ReceiveChat = {
    type: 'RECEIVE_CHAT'
    chat: Api.ChatMetadata
}

export type ReceiveClaimedChat = {
    type: 'RECEIVE_CLAIMED_CHAT'
    chat: Api.ChatMetadata
}

export type ReceiveAmplifyChatMetadataSchedulingAppointments = {
    type: 'RECEIVE_AMPLIFY_CHAT_METADATA_SCHEDULING_APPOINTMENTS'
    chatId: string
    appointments: ApiV2.Amplify.SchedulingAppointment[]
}

export type ReceiveClaimedChats = {
    type: 'RECEIVE_CLAIMED_CHATS'
    chats: Api.ChatMetadata[]
}

export type ReceiveUnclaimedChats = {
    type: 'RECEIVE_UNCLAIMED_CHATS'
    chats: Api.ChatMetadata[]
}

export type ReceiveClosedChat = {
    type: 'RECEIVE_CLOSED_CHAT'
    chatChannelName: string
}

export type ReceiveLatestChats = {
    type: 'RECEIVE_LATEST_CHATS'
    chats: Api.ChatMetadata[]
}

export type ReceiveChatMessage = {
    type: 'RECEIVE_CHAT_MESSAGE'
    message: Models.Message
    channel: string
    chat?: Models.ChatMetadata
}

export type ReceiveChatHistory = {
    type: 'RECEIVE_CHAT_HISTORY'
    channelName: string
    history: PubNub.HistoryResponse<Services.PubNub.Message>
}

export type ReceiveHistoryBatch = {
    type: 'RECEIVE_HISTORY_BATCH'
    history: PubNub.BatchHistoryResponse<Services.PubNub.Message>
}

export type ReceiveAuthKey = {
    type: 'RECEIVE_AUTH_KEY'
    authKey: string
}

export type ReceiveMessagePreview = {
    type: 'RECEIVE_MESSAGE_PREVIEW'
    chat: string
    preview: string
}

export type ReceiveSurvey = {
    type: 'RECEIVE_SURVEY'
    surveyId: string
    responses: Array<Api.Survey.Response>
    practiceSpecialtyValue: Models.PracticeSpecialtyValue
    webCodeKey: string
}

export type SetIdleTimer = {
    type: 'SET_IDLE_TIMER'
    chat: Models.ChatMetadata
    idleTimer: number
}

export type SetInactiveTimer = {
    type: 'SET_INACTIVE_TIMER'
    chat: Models.ChatMetadata
    inactiveTimer: number
}

export type IncrementCount = {
    type: 'INCREMENT_BROWSING_COUNT'
}

export type DecrementCount = {
    type: 'DECREMENT_BROWSING_COUNT'
}

export type PatientRejoinedChat = {
    type: 'PATIENT_REJOINED_CHAT'
    chat: Models.ChatMetadata
}

export type SetPatientOnlineStatus = {
    type: 'SET_PATIENT_ONLINE_STATUS'
    chat: Models.ChatMetadata
    isOnline: boolean
}

export type SetSelectedChat = {
    type: 'SET_SELECTED_CHAT'
    chat: Models.ChatMetadata
}

export type SetSelectedChats = {
    type: 'SET_SELECTED_CHATS'
    chats: Models.ChatCenterSelectedChat[]
}

export type SetSelectedChatTab = {
    type: 'SET_SELECTED_CHAT_TAB'
    chatId: string
    tab: ChatCenterTab
}

export type UpdateSelectedChat = {
    type: 'UPDATE_SELECTED_CHAT'
    chatUpdate: Models.UpdateChatCenterSelectedChat
}

export type ReceiveShortcuts = {
    type: 'RECEIVE_SHORTCUTS'
    shortcuts: Models.Shortcut[]
}

export type CreateShortcut = {
    type: 'CREATE_SHORTCUT'
    shortcut: Models.Shortcut
}

export type UpdateShortcut = {
    type: 'UPDATE_SHORTCUT'
    shortcut: Models.Shortcut
}

export type RemoveShortcut = {
    type: 'REMOVE_SHORTCUT'
    shortcut: Models.Shortcut
}

export type UpdateNetworkState = {
    type: 'UPDATE_NETWORK_STATE'
    isNetworkUp: boolean
}

export type TurnChatsPage = {
    type: 'TURN_CHATS_PAGE'
    paginator: Partial<Models.ChatCenterPaginator>
}

export type ReceiveChatsOnScreen = {
    type: 'RECEIVE_CHATS_ON_SCREEN'
    numChatsOnScreen: number
}

export type UpdateConnectPayerId = {
    type: 'UPDATE_CHAT_CONNECT_PAYER_ID'
    chat: Models.ChatMetadata
    payerId: string
}

export type ShowPatientNameRequiredAlert = {
    type: 'SHOW_PATIENT_NAME_REQUIRED_ALERT'
    chatId: string
}

export type ClosePatientNameRequiredAlert = {
    type: 'CLOSE_PATIENT_NAME_REQUIRED_ALERT'
    chatId: string
}

export function clearIdleTimer(channel: string): ClearIdleTimer {
    return {
        type: 'CLEAR_IDLE_TIMER',
        channel,
    }
}

export function clearInactiveTimer(channel: string): ClearInactiveTimer {
    return {
        type: 'CLEAR_INACTIVE_TIMER',
        channel,
    }
}

export function receiveChat(chat: Api.ChatMetadata): ReceiveChat {
    return {
        type: 'RECEIVE_CHAT',
        chat,
    }
}

export function receiveClaimedChat(chat: Api.ChatMetadata): ReceiveClaimedChat {
    return {
        type: 'RECEIVE_CLAIMED_CHAT',
        chat,
    }
}

export function receiveAmplifyChatMetadataSchedulingAppointments(
    chatId: string,
    appointments: ApiV2.Amplify.SchedulingAppointment[],
): ReceiveAmplifyChatMetadataSchedulingAppointments {
    return {
        type: 'RECEIVE_AMPLIFY_CHAT_METADATA_SCHEDULING_APPOINTMENTS',
        chatId,
        appointments,
    }
}

export function receiveClaimedChats(chats: Api.ChatMetadata[]): ReceiveClaimedChats {
    return {
        type: 'RECEIVE_CLAIMED_CHATS',
        chats,
    }
}

export function receiveUnclaimedChats(chats: Api.ChatMetadata[]): ReceiveUnclaimedChats {
    return {
        type: 'RECEIVE_UNCLAIMED_CHATS',
        chats,
    }
}

export function receiveClosedChat(chatChannelName: string): ReceiveClosedChat {
    return {
        type: 'RECEIVE_CLOSED_CHAT',
        chatChannelName,
    }
}

export function receiveLatestChats(chats: Array<Api.ChatMetadata>): ReceiveLatestChats {
    return {
        type: 'RECEIVE_LATEST_CHATS',
        chats,
    }
}

export function receiveBrowsingCount(count: number): ReceiveBrowsingCount {
    return {
        type: 'RECEIVE_BROWSING_COUNT',
        count,
    }
}

export function receiveChatMessage(channel: string, message: Models.Message): ReceiveChatMessage {
    return {
        type: 'RECEIVE_CHAT_MESSAGE',
        message,
        channel,
    }
}

export function receiveChatHistory(
    channelName: string,
    history: PubNub.HistoryResponse<Services.PubNub.Message>,
): ReceiveChatHistory {
    return {
        type: 'RECEIVE_CHAT_HISTORY',
        channelName,
        history,
    }
}

export function receiveHistoryBatch(
    history: PubNub.BatchHistoryResponse<Services.PubNub.Message>,
): ReceiveHistoryBatch {
    return {
        type: 'RECEIVE_HISTORY_BATCH',
        history,
    }
}

export function receiveAuthKey(authKey: string): ReceiveAuthKey {
    return {
        type: 'RECEIVE_AUTH_KEY',
        authKey,
    }
}

export function receiveMessagePreview(chat: string, preview: string): ReceiveMessagePreview {
    return {
        type: 'RECEIVE_MESSAGE_PREVIEW',
        chat,
        preview,
    }
}

export function receiveSurvey(
    surveyId: string,
    responses: Api.Survey.Response[],
    practiceSpecialtyValue: Models.PracticeSpecialtyValue,
    webCodeKey: string,
): ReceiveSurvey {
    return {
        type: 'RECEIVE_SURVEY',
        surveyId,
        responses,
        practiceSpecialtyValue,
        webCodeKey,
    }
}

export function receiveShortcuts(shortcuts: Models.Shortcut[]): ReceiveShortcuts {
    return {
        type: 'RECEIVE_SHORTCUTS',
        shortcuts,
    }
}

export function createShortcut(shortcut: Models.Shortcut): CreateShortcut {
    return {
        type: 'CREATE_SHORTCUT',
        shortcut,
    }
}

export function updateShortcut(shortcut: Models.Shortcut): UpdateShortcut {
    return {
        type: 'UPDATE_SHORTCUT',
        shortcut,
    }
}

export function removeShortcut(shortcut: Models.Shortcut): RemoveShortcut {
    return {
        type: 'REMOVE_SHORTCUT',
        shortcut,
    }
}

export function setIdleTimer(chat: Models.ChatMetadata, idleTimer: number): SetIdleTimer {
    return {
        type: 'SET_IDLE_TIMER',
        chat,
        idleTimer,
    }
}

export function setInactiveTimer(chat: Models.ChatMetadata, inactiveTimer: number): SetInactiveTimer {
    return {
        type: 'SET_INACTIVE_TIMER',
        chat,
        inactiveTimer,
    }
}

export function setSelectedChat(chat: Models.ChatMetadata): SetSelectedChat {
    return {
        type: 'SET_SELECTED_CHAT',
        chat,
    }
}

export function setSelectedChats(chats: Models.ChatCenterSelectedChat[]): SetSelectedChats {
    return {
        type: 'SET_SELECTED_CHATS',
        chats,
    }
}

export function setSelectedChatTab(chatId: string, tab: ChatCenterTab): SetSelectedChatTab {
    return {
        type: 'SET_SELECTED_CHAT_TAB',
        chatId,
        tab,
    }
}

export function updateSelectedChat(chatUpdate: Models.UpdateChatCenterSelectedChat): UpdateSelectedChat {
    return {
        type: 'UPDATE_SELECTED_CHAT',
        chatUpdate,
    }
}

export function incrementCount(): IncrementCount {
    return {
        type: 'INCREMENT_BROWSING_COUNT',
    }
}

export function decrementCount(): DecrementCount {
    return {
        type: 'DECREMENT_BROWSING_COUNT',
    }
}

export function patientRejoinedChat(chat: Models.ChatMetadata): PatientRejoinedChat {
    return {
        type: 'PATIENT_REJOINED_CHAT',
        chat,
    }
}

export function setPatientOnlineStatus(chat: Models.ChatMetadata, isOnline: boolean): SetPatientOnlineStatus {
    return {
        type: 'SET_PATIENT_ONLINE_STATUS',
        chat,
        isOnline,
    }
}

export function updateNetworkState(isNetworkUp: boolean): UpdateNetworkState {
    return {
        type: 'UPDATE_NETWORK_STATE',
        isNetworkUp,
    }
}

export function receiveChatsOnScreen(numChatsOnScreen: number): ReceiveChatsOnScreen {
    return {
        type: 'RECEIVE_CHATS_ON_SCREEN',
        numChatsOnScreen,
    }
}

export function turnChatsPage(paginator: Partial<Models.ChatCenterPaginator>): TurnChatsPage {
    return {
        type: 'TURN_CHATS_PAGE',
        paginator,
    }
}

export function updateConnectPayerId(chat: Models.ChatMetadata, payerId: string): UpdateConnectPayerId {
    return {
        type: 'UPDATE_CHAT_CONNECT_PAYER_ID',
        chat,
        payerId,
    }
}

export function showPatientNameRequiredAlert(chatId: string): ShowPatientNameRequiredAlert {
    return {
        type: 'SHOW_PATIENT_NAME_REQUIRED_ALERT',
        chatId,
    }
}

export function closePatientNameRequiredAlert(chatId: string): ClosePatientNameRequiredAlert {
    return {
        type: 'CLOSE_PATIENT_NAME_REQUIRED_ALERT',
        chatId,
    }
}

export function fetchBrowsingCount() {
    return async (dispatch: any) => {
        try {
            const count = await Api.Amplify.getBrowsingCount()

            if (!isNaN(Number(count))) {
                await dispatch(receiveBrowsingCount(count))
            }

            return count
        } catch (e) {
            getGlobalStore().dispatch(
                error({
                    message: `Error fetching browsing count.`,
                    position: 'tr',
                    autoDismiss: 7,
                }),
            )
            return null
        }
    }
}

export function fetchClaimedChats(): any {
    return async (dispatch: any) => {
        const chats = await Api.Amplify.listClaimedChats()

        if (_.isEmpty(chats)) {
            return
        }

        await dispatch(receiveClaimedChats(chats))
    }
}

export function fetchLatestChats(): any {
    return async (dispatch: any) => {
        try {
            const chats = await Api.Amplify.listLatestChats()
            if (Array.isArray(chats)) {
                await dispatch(receiveLatestChats(chats))
            }
        } catch (e) {
            return e
        }
    }
}

export function fetchUnclaimedChats(): any {
    return async (dispatch: any) => {
        const chats = await Api.Amplify.listUnclaimedChats()
        await dispatch(receiveUnclaimedChats(chats))
    }
}

export function fetchChatHistory(channelName: string, conversation?: Models.Chat): any {
    return async (dispatch: any, getState: any) => {
        const firstMessage = conversation && _.first(conversation.messages)
        const timetoken = firstMessage && firstMessage.timetoken
        const accountId = getState().app.self?.account.id || 'none'
        const pubnubService = PubNubService.getInstance(accountId)

        const history = await pubnubService.getChatHistory(channelName, timetoken)

        await dispatch(receiveChatHistory(channelName, history))
    }
}

export function checkPatientOnlineStatus(chat: Models.ChatMetadata): any {
    return async (dispatch: any, getState: any) => {
        const accountId = getState().app.self?.account.id || 'none'
        const pubnubService = PubNubService.getInstance(accountId)
        const hereNowResponse = await pubnubService.hereNow(chat.channelName)

        if (hereNowResponse && hereNowResponse.totalOccupancy > 0) {
            let patientIsOnline = false
            hereNowResponse.channels[chat.channelName].occupants.forEach((occupant: any) => {
                if (occupant.uuid === chat?.meta?.pubnub_user_id) {
                    patientIsOnline = true
                }
            })

            dispatch(setPatientOnlineStatus(chat, patientIsOnline))
        }
    }
}

export function fetchAllNewerChatMsgs(channelName: string): any {
    return async (dispatch: any, getState: any) => {
        const accountId = getState().app.self?.account.id || 'none'
        const pubnubService = PubNubService.getInstance(accountId)
        const history = await pubnubService.getAllChatHistory(1, channelName, undefined, undefined)

        await dispatch(receiveChatHistory(channelName, history))
    }
}

export function fetchNewerChatMsgs(channelName: string, conversation?: Models.Chat): any {
    return async (dispatch: any, getState: any) => {
        const firstMessage = conversation && _.last(conversation.messages)
        const timetoken = firstMessage && firstMessage.timetoken
        const accountId = getState().app.self?.account.id || 'none'
        const pubnubService = PubNubService.getInstance(accountId)
        const history = await pubnubService.getChatHistory(channelName, undefined, timetoken)
        await dispatch(receiveChatHistory(channelName, history))
    }
}

export function setMessagePreview(channel: string, preview: string) {
    return async (dispatch: any) => {
        await dispatch(receiveMessagePreview(channel, preview))
    }
}

export function fetchAuthKey() {
    return async (dispatch: any) => {
        const authKey = await Api.Amplify.fetchAuthKey()
        await dispatch(receiveAuthKey(authKey))
    }
}

export function claimChat(account: Models.Account, chat: Models.ChatMetadata): any {
    return async (dispatch: any, getState: any) => {
        try {
            const updatedChat = await Api.Amplify.putChatMetadataUpdate(chat, {
                status: 'claimed',
                claimee_id: account.id,
            })
            const accountId = getState().app.self?.account.id || 'none'
            const pubnubService = PubNubService.getInstance(accountId)
            if (updatedChat?.id) {
                pubnubService.subscribe([chat.channelName, `whisper_${chat.channelName}`])
                await dispatch(receiveChat(updatedChat))
                await dispatch(receiveClaimedChat(updatedChat))
                pubnubService.fireEvent('specialist:claim_chat', updatedChat)
            }
        } catch (err) {
            return err
        } finally {
            await dispatch(fetchUnclaimedChats())
            await dispatch(fetchChatHistory(chat.channelName))
        }
    }
}

export function fetchAmplifyChatMetadataSchedulingAppointments(chatId: string): any {
    return async (dispatch: any) => {
        try {
            const appointments = await ApiV2.Amplify.getAmplifyChatMetadataSchedulingAppointments(chatId)

            await dispatch(receiveAmplifyChatMetadataSchedulingAppointments(chatId, appointments))
        } catch (err) {
            return err
        }
    }
}

export function closeChat(chat: Models.ChatMetadata, close: Models.ChatClose) {
    return async (dispatch: any, getState: any) => {
        try {
            const updatedChat = await ApiV2.Amplify.postCloseAmplifyChatMetadata(chat.id, close)
            await dispatch(receiveChat(updatedChat))
            await dispatch(receiveClosedChat(updatedChat.channel_name))
            const accountId = getState().app.self?.account.id || 'none'
            const pubnubService = PubNubService.getInstance(accountId)
            await pubnubService.send(chat.channelName, {
                id: newObjectId(32),
                text: `This chat has been closed by ${chat.claimee && chat.claimee.firstName}.`,
                is_patient: false,
                sender_id: pubnubService.getChatterId(),
                type: 'system:closed',
                claimee_name: chat.claimee && chat.claimee.firstName,
            })
            await pubnubService.fireEvent('specialist:close_chat', updatedChat)
            pubnubService.unsubscribe([chat])

            await dispatch(clearIdleTimer(chat.channelName))
            await dispatch(clearInactiveTimer(chat.channelName))

            return updatedChat
        } catch (err) {
            if (err.errors?.find((e: ErrorRecord) => e.errorCode === 45010))
                dispatch(showPatientNameRequiredAlert(chat.id))
            return err
        }
    }
}

export function setChatState(chat: Models.ChatMetadata, status: Models.ChatStatusValue) {
    return async (dispatch: any) => {
        const updated = await Api.Amplify.putChatMetadataUpdate(chat, { status })
        await dispatch(receiveChat(updated))
        await dispatch(receiveClaimedChat(updated))
    }
}

export function saveChat(chat: Models.ChatMetadata, changes: Api.ChatMetadataUpdate) {
    return async (dispatch: any, getState: any) => {
        const updatedChat = await Api.Amplify.putChatMetadataUpdate(chat, changes)
        await dispatch(resetToClaimed(chat))
        await dispatch(receiveChat(updatedChat))
        await dispatch(receiveClaimedChat(updatedChat))
        const accountId = getState().app.self?.account.id || 'none'
        const pubnubService = PubNubService.getInstance(accountId)
        pubnubService.fireEvent('specialist:modify_chat_name', updatedChat)
    }
}

export function fetchSurvey(surveyParams: Models.GetSurveyResponse) {
    return async (dispatch: any) => {
        try {
            const response = await Api.Survey.getResponses(surveyParams)

            if (response.statusCode === 500) {
                throw new Error('There was an error with server. Please reload request')
            } else if (response.length === 0) {
                throw new Error('Requested survey not found with associated Survey ID.')
            } else {
                await dispatch(
                    receiveSurvey(
                        surveyParams.id,
                        response,
                        surveyParams.practiceSpecialtyValue,
                        surveyParams.webCodeKey,
                    ),
                )
            }
        } catch (err) {
            throw err
        }
    }
}

export function fetchShortcuts(searchTerms: Api.Amplify.ShortcutSearchTerms = {}) {
    return async (dispatch: any) => {
        const shortcuts = await Api.Amplify.listShortcuts(searchTerms)
        await dispatch(receiveShortcuts(shortcuts))
    }
}

export function addShortcut(shortcut: Models.Shortcut) {
    return async (dispatch: any) => {
        const newShortcut = await Api.Amplify.postShortcut(shortcut)
        await dispatch(createShortcut(newShortcut))
    }
}

export function editShortcut(shortcut: Models.Shortcut) {
    return async (dispatch: any) => {
        const updatedShortcut = await Api.Amplify.putShortcut(shortcut)
        await dispatch(updateShortcut(updatedShortcut))
    }
}

export function deleteShortcut(shortcutId: string) {
    return async (dispatch: any) => {
        const removedShortcut = await Api.Amplify.deleteShortcut(shortcutId)
        await dispatch(removeShortcut(removedShortcut))
    }
}

export function handleMessage(message: PubNub.MessageEvent<Services.PubNub.Message>, chat?: Models.ChatMetadata) {
    return async (dispatch: any, getState: () => RootState) => {
        const accountId = getState().app.self?.account.id || 'none'
        const pubnubService = PubNubService.getInstance(accountId)
        await dispatch(
            receiveChatMessage(message.channel, {
                ...message.message,
                timetoken: message.timetoken,
                status: 'success',
            } as Models.Message),
        )

        if (chat) {
            await dispatch(clearIdleTimer(message.channel))

            if (message.message.type === 'system:closed' && message.message.is_patient) {
                const loadedChat = await Api.Amplify.getChatMetadata(chat)
                const updatedChat = { ...loadedChat, status: ChatStatus.PatientClosed }

                await dispatch(receiveChat(updatedChat))
                await dispatch(receiveClaimedChat(updatedChat))
                await dispatch(updateSelectedChat({ id: updatedChat.id, status: ChatStatus.PatientClosed }))
                await dispatch(clearInactiveTimer(message.channel))

                pubnubService.unsubscribe([mapChatMetadata(loadedChat)])
                return
            }

            if (message.message.type === 'system:connect-payment-card-verified') {
                if (chat.connectPayerId) {
                    await dispatch(postPatientConsentToPaymentTerms(chat.practice.id, chat.connectPayerId))
                }
                await dispatch(updateConnectPaymentStatus(chat, ConnectPaymentStatus.Verified))
                return
            }

            if (message.message.type === 'system:connect-payment-form-closed-by-user') {
                const currentPaymentStatus = getState().bookings.connect[chat.id]?.paymentStatus
                if (currentPaymentStatus !== ConnectPaymentStatus.Verified) {
                    await dispatch(updateConnectPaymentStatus(chat, ConnectPaymentStatus.Canceled))
                }
                return
            }

            if (chat.status.value === 'idle') {
                await dispatch(setChatState(chat, 'claimed'))
            }

            if (message.publisher === pubnubService.getChatterId() && chat.status.value !== 'idle') {
                const idleTimer = window.setTimeout(
                    () => dispatch(setChatState(chat, 'idle')),
                    moment.duration(30, 'seconds').asMilliseconds(),
                )

                const inactiveTimer = window.setTimeout(
                    () => dispatch(setChatState(chat, 'inactive')),
                    moment.duration(5, 'minutes').asMilliseconds(),
                )

                await dispatch(setIdleTimer(chat, idleTimer))
                await dispatch(setInactiveTimer(chat, inactiveTimer))
            } else {
                await dispatch(resetToClaimed(chat))
            }
        }
    }
}

export function resetToClaimed(chat: Models.ChatMetadata) {
    return async (dispatch: any) => {
        await dispatch(clearIdleTimer(chat.channelName))
        await dispatch(clearInactiveTimer(chat.channelName))

        if (chat.status.value === 'idle' || chat.status.value === 'inactive') {
            await dispatch(setChatState(chat, 'claimed'))
        }
    }
}

export function handleEvent(event: PubNub.MessageEvent<Services.PubNub.Event>) {
    return async (dispatch: any, getState: any) => {
        const accountId = getState().app.self?.account.id || 'none'
        const pubnubService = PubNubService.getInstance(accountId)

        switch (event.message.type) {
            case 'patient:new_inbound': {
                // await dispatch(incrementCount())
                break
            }
            case 'patient:first_message': {
                await dispatch(receiveChat(event.message.payload))
                NotificationService.notify('New Patient')
                // await dispatch(decrementCount())
                break
            }
            case 'specialist:claim_chat':
            case 'specialist:modify_chat_name': {
                await dispatch(receiveChat(event.message.payload))
                break
            }
            case 'specialist:close_chat': {
                await dispatch(receiveChat(event.message.payload))
                await dispatch(receiveClosedChat(event.message.payload))
                pubnubService.unsubscribe([mapChatMetadata(event.message.payload)])
                break
            }
            default: {
                return
            }
        }
    }
}

export function setNetworkState(isNetworkUp: boolean) {
    return async (dispatch: any) => {
        await dispatch(updateNetworkState(isNetworkUp))
    }
}

export function setChatsOnScreen(numChatsOnScreen: number) {
    return async (dispatch: any) => {
        await dispatch(receiveChatsOnScreen(numChatsOnScreen))
    }
}
